import { useEffect, useId, useState } from "react";
import { Dropdown, DropdownItem } from "react-migration/components/Dropdown";
import { Field } from "react-migration/components/Field/Field";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";

export type HometrackSaleDisplayBy = "IndexedCurrentValue" | "LastKnownValue";
export type HometrackRentalDisplayBy = "IndexedCurrentValue" | "AskingRent";

export type HometrackDisplayBy<T extends "sale" | "rent"> = T extends "sale"
  ? HometrackSaleDisplayBy
  : HometrackRentalDisplayBy;

export type HometrackLayerFilterState<T extends "sale" | "rent" = "sale"> = {
  displayBy: HometrackDisplayBy<T>;
  bedrooms: number;
};

type HometrackLayerFiltersProps<T extends "sale" | "rent" = "sale"> = {
  type: T;
  filters: HometrackLayerFilterState<T>;
  onFilterChange: (filters: HometrackLayerFilterState<T>) => void;
};

export const HometrackLayerFilters = <T extends "sale" | "rent">({
  type,
  filters,
  onFilterChange,
}: HometrackLayerFiltersProps<T>) => {
  const [displayBy, setDisplayBy] = useState<HometrackDisplayBy<T>>(filters.displayBy as HometrackDisplayBy<T>);
  const [bedrooms, setBedrooms] = useState(filters.bedrooms);

  const handleDisplayByChange = (value: HometrackDisplayBy<T>) => {
    logEventV2({
      name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
      properties: { cardName: "comparables", filterName: "displayBy", filterState: value },
    });
    setDisplayBy(value);
  };

  const handleBedroomsChange = (value: number) => {
    logEventV2({
      name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
      properties: { cardName: "comparables", filterName: "bedrooms", filterState: value },
    });
    setBedrooms(Math.floor(value));
  };

  useEffect(() => {
    onFilterChange({ displayBy, bedrooms } as HometrackLayerFilterState<T>);
  }, [displayBy, bedrooms, onFilterChange]);

  const id = useId();
  const { t } = useTranslation();

  // Define typed dropdown options for each type
  const displayBySaleOptions: DropdownItem<HometrackSaleDisplayBy>[] = [
    {
      label: t("comparables.filters.displayBy.zoopla_valuation_per_unit_last"),
      description: (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_valuation_per_unit_last.description")}
        </span>
      ),
      value: "LastKnownValue",
    },
    {
      label: t("comparables.filters.displayBy.zoopla_valuation_current"),
      description: (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_valuation_current.description")}
        </span>
      ),
      value: "IndexedCurrentValue",
    },
  ];

  const displayByRentalOptions: DropdownItem<HometrackRentalDisplayBy>[] = [
    {
      label: t("comparables.filters.displayBy.zoopla_rental_asking"),
      description: (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_rental_asking.description")}
        </span>
      ),
      value: "AskingRent",
    },
    {
      label: t("comparables.filters.displayBy.zoopla_rental_estimate"),
      description: (
        <span className="text-xs text-neutral-500">
          {t("comparables.filters.displayBy.zoopla_rental_estimate.description")}
        </span>
      ),
      value: "IndexedCurrentValue",
    },
  ];

  // Get the appropriate options based on the type
  const options =
    type === "sale"
      ? (displayBySaleOptions as DropdownItem<HometrackDisplayBy<T>>[])
      : (displayByRentalOptions as DropdownItem<HometrackDisplayBy<T>>[]);

  return (
    <div className="flex items-center w-full gap-x-2 ">
      <div className="flex items-center flex-1 gap-x-2">
        <label htmlFor="hometrack-valuation-display-by">Display by</label>
        <Dropdown
          id="hometrack-valuation-display-by"
          placeholder="Display by"
          classNameContainer="flex-1 whitespace-nowrap"
          optionsContainerClassName="w-[300px] whitespace-normal"
          options={options}
          value={displayBy}
          handleValueChange={(value) => {
            handleDisplayByChange(value.value as HometrackDisplayBy<T>);
          }}
        />
      </div>
      <div className="relative flex items-center gap-x-2">
        <label htmlFor={`${id}-bedrooms-input`}>Bedrooms</label>
        <Field.Input
          id={`${id}-bedrooms-input`}
          key={`${id}-bedrooms-input`}
          className="z-20 grow-0 shrink-0 w-[64px] h-[32px]"
          value={bedrooms}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            if (inputValue === "" || /^\d+$/.test(inputValue)) {
              handleBedroomsChange(Number(inputValue));
            }
          }}
          step={1}
          type="number"
          inputMode="numeric"
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "." || e.key === ",") {
              e.preventDefault();
            }
          }}
        />
      </div>
    </div>
  );
};
